import * as React from "react";
import { CallToActionFlexComponent, CallToActionProps } from "./typings";
import { UitkCardAloha, UitkCardAlohaContentSection, UitkCardAlohaLink } from "uitk-react-cards";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkText } from "uitk-react-text";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { inject, observer } from "mobx-react";
import { AspectRatioEnum, ThumborStore } from "stores/thumbor/ThumborStore";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { useLocalization } from "@shared-ui/localization-context";
import { locKeys } from "./l10n/locKeys";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { FlexComponent } from "typings/flexFramework/FlexComponent";
import { withMabTracker, MabTrackEvent } from "components/utility/analytics/MabTracker";

const ENTER_BUTTON_KEY_VALUE = "Enter";
const SPACE_BUTTON_KEY_VALUE = " ";

const CardMediaImage = inject("thumbor")(
  ({
    thumbor,
    flexComponent,
    context,
  }: {
    thumbor?: ThumborStore;
    flexComponent: CallToActionFlexComponent;
    context: ExtendedContextStore;
  }) => {
    const media = flexComponent.model?.callToAction?.media?.[0];

    if (thumbor === undefined || !media) {
      return null;
    }

    return (
      <UitkImage
        alt={flexComponent.model.callToAction.media[0].mediaAlt}
        src={safelyResizeImage(flexComponent, context, thumbor)}
      />
    );
  }
);

export const safelyResizeImage = (
  flexComponent: CallToActionFlexComponent,
  context: ExtendedContextStore,
  thumbor: ThumborStore
): string => {
  return thumbor.safelyGenerateThumborizedImageUrl(
    flexComponent.model.callToAction.media[0].mediaUrl,
    1040,
    580,
    context,
    imageRatio(flexComponent.model.imageAspectRatio),
    true,
    60
  );
};

const imageRatio = (ratio: string) => {
  switch (ratio) {
    case "1-1": {
      return AspectRatioEnum.R_1_1;
    }
    case "3-2": {
      return AspectRatioEnum.R_3_2;
    }
    case "4-3": {
      return AspectRatioEnum.R_4_3;
    }
    case "21-9": {
      return AspectRatioEnum.R_21_9;
    }
    default: {
      return AspectRatioEnum.DEFAULT;
    }
  }
};

const uitkFigureAspectRatio = (ratio: string) => {
  switch (ratio) {
    case "1-1": {
      return UitkFigureAspectRatioType.R1_1;
    }
    case "3-2": {
      return UitkFigureAspectRatioType.R3_2;
    }
    case "3-4": {
      return UitkFigureAspectRatioType.R3_4;
    }
    case "4-3": {
      return UitkFigureAspectRatioType.R4_3;
    }
    case "21-9": {
      return UitkFigureAspectRatioType.R21_9;
    }
    default: {
      return UitkFigureAspectRatioType.R16_9;
    }
  }
};

const matchAffinityToLocKey = (callToActionName: string): string => {
  return locKeys.has(callToActionName) ? locKeys.get(callToActionName) || "" : "";
};

const getViewSettings = (flexComponent: FlexComponent, formatText: (key: string, ...args: any[]) => string) => {
  const moduleName = flexComponent.name;
  const editorialMargins = "all-t-margin-three m-b-margin-three l-b-margin-three xl-b-margin-three";
  const snippetMargins = "all-t-margin-three l-b-margin-three xl-b-margin-three";
  const callToActionName = flexComponent.model.title && flexComponent.model.title.replace(" ", "_");
  const rfrr = `${moduleName}.${callToActionName}.${Action.CLICK}`;
  let showEditorialContent = false;
  let cardClassName = "";
  let figureClassName = "";
  let cardScrimType = UitkScrimType.OVERLAY;
  let callToActionLabel = "";
  let callToActionSubLabel = "";
  let callToActionTitle = flexComponent.model.title;
  let callToActionLink = flexComponent.model.callToAction?.callToActionButton?.url;
  let uitkClassNames = "all-y-gutter-three";
  let preventDefaultOnClick = false;
  let tabIndex;

  switch (flexComponent.model.view) {
    case "affinity": {
      cardClassName = "cardAffinityHeading uitk-type-heading-400";
      figureClassName = "uitk-card-aloha-roundcorner-all";
      callToActionLabel = flexComponent.model.callToAction?.callToActionButton?.label;
      callToActionLink = `${callToActionLink}/?rfrr=${rfrr}`;
      break;
    }
    case "theme-selector": {
      cardClassName = "cardAffinityHeading uitk-type-heading-600";
      figureClassName = "uitk-card-aloha-roundcorner-all";
      if (flexComponent.model.callToAction.callToActionButton.label === "l10n") {
        callToActionLabel = formatText(matchAffinityToLocKey(callToActionName.toLowerCase()));
        callToActionTitle = callToActionLabel;
      } else {
        callToActionLabel = flexComponent.model.callToAction?.callToActionButton?.label;
      }
      preventDefaultOnClick = true;
      tabIndex = 0;
      break;
    }
    case "editorial": {
      cardClassName = "cardEditorialHeading uitk-type-heading-500 uitk-type-left";
      figureClassName = "uitk-card-aloha-roundcorner-top-left uitk-card-aloha-roundcorner-top-right";
      cardScrimType = UitkScrimType.BOTTOM;
      uitkClassNames = editorialMargins;
      showEditorialContent = !!flexComponent.model.callToAction.text;
      callToActionLabel = flexComponent.model.title;
      callToActionSubLabel = flexComponent.model.subtitle;
      callToActionLink = `${callToActionLink}/?rfrr=${rfrr}`;
      break;
    }
    case "snippet": {
      cardClassName = "cardEditorialHeading uitk-type-heading-500 uitk-type-left";
      figureClassName = "uitk-card-aloha-roundcorner-top-left uitk-card-aloha-roundcorner-bottom-left";
      cardScrimType = UitkScrimType.BOTTOM;
      uitkClassNames = snippetMargins;
      showEditorialContent = !!flexComponent.model.callToAction.text;
      callToActionLabel = flexComponent.model.title;
      callToActionLink = `${callToActionLink}/?rfrr=${rfrr}`;
      break;
    }
    case "full-bleed-vertical": {
      cardClassName = "cardEditorialHeading uitk-type-heading-500 uitk-type-left";
      figureClassName = "uitk-card-aloha-roundcorner-all";
      cardScrimType = UitkScrimType.BOTTOM;
      uitkClassNames = editorialMargins;
      showEditorialContent = !!flexComponent.model.callToAction.text;
      callToActionLabel = flexComponent.model.title;
      callToActionSubLabel = flexComponent.model.subtitle;
      callToActionLink = `${callToActionLink}/?rfrr=${rfrr}`;
      break;
    }
  }

  return {
    cardClassName,
    figureClassName,
    cardScrimType,
    callToActionLabel,
    callToActionSubLabel,
    callToActionTitle,
    callToActionLink,
    uitkClassNames,
    showEditorialContent,
    preventDefaultOnClick,
    tabIndex,
  };
};

export const CallToAction = withMabTracker(
  inject(
    "analytics",
    "compositionStore",
    "themeSelectorStore"
  )(
    observer((props: CallToActionProps) => {
      if (props.flexComponent.model.ajaxOnly) {
        return null;
      }

      const { formatText } = useLocalization();
      const { flexComponent, context, analytics, compositionStore } = props;
      const viewProps = getViewSettings(flexComponent, formatText);

      const keyboardPress = (e: React.KeyboardEvent) => {
        //button elements should allow click on enter or space when a keydown event occurs. Allow by default when a mouse event.
        const allowClick = e.key === ENTER_BUTTON_KEY_VALUE || e.key === SPACE_BUTTON_KEY_VALUE;

        if (allowClick) {
          cardClicked(e);
        }
      };

      const cardClicked = (e: React.SyntheticEvent) => {
        const rfrr = `nativeMarqueeTile.tile.click`;

        if (compositionStore && flexComponent.model.nextScene) {
          props.themeSelectorStore.updateActiveTheme(flexComponent.model.affinity);
          compositionStore.changeScene(flexComponent.model.nextScene);
        }

        const trackingInfo: FlexTrackingInfo = {
          moduleName: flexComponent.name,
          rfrr,
          action: Action.CLICK,
        };

        if (props.mabTrack) {
          const mabTrackData: MabTrackEvent = {
            moduleName: "call-to-action",
            eventTarget: "call-to-action",
            eventType: "homepageClick.nonPath",
            rfrr,
          };
          props.mabTrack(mabTrackData);
        }

        sendDelayedTrackEvent(trackingInfo, analytics);

        if (viewProps.preventDefaultOnClick) {
          e.preventDefault();
        }
      };

      return (
        <div
          id={flexComponent.id}
          data-fm={flexComponent.fmId}
          data-fm-title-id={flexComponent.fmTitleId}
          className={`uitk-cell uitk-type-center CallToAction all-cell-1-1 ${viewProps.uitkClassNames}`}
          onClick={cardClicked}
          onKeyDown={keyboardPress}
          tabIndex={viewProps.tabIndex}
        >
          <UitkCardAloha overflow={true}>
            {flexComponent.model.view === "snippet" ? (
              <UitkLayoutGrid columns={4}>
                <>
                  <UitkLayoutGridItem colSpan={1}>
                    <UitkFigure
                      ratio={uitkFigureAspectRatio(flexComponent.model.imageAspectRatio)}
                      className={viewProps.figureClassName}
                    >
                      <CardMediaImage flexComponent={flexComponent} context={context} />
                      <UitkScrim type={viewProps.cardScrimType}>
                        <h3 className={viewProps.cardClassName}>{viewProps.callToActionLabel}</h3>
                        <div className="uitk-type-left uitk-type-subheading">{viewProps.callToActionSubLabel}</div>
                      </UitkScrim>
                    </UitkFigure>
                  </UitkLayoutGridItem>
                  <UitkLayoutGridItem colSpan={3}>
                    {viewProps.showEditorialContent && (
                      <UitkCardAlohaContentSection>
                        <UitkText size={200}>
                          <div
                            className="uitk-type-left"
                            dangerouslySetInnerHTML={{ __html: flexComponent.model.callToAction.text }}
                          />
                        </UitkText>
                      </UitkCardAlohaContentSection>
                    )}
                  </UitkLayoutGridItem>
                </>
              </UitkLayoutGrid>
            ) : (
              <>
                <UitkFigure
                  ratio={uitkFigureAspectRatio(flexComponent.model.imageAspectRatio)}
                  className={viewProps.figureClassName}
                >
                  <CardMediaImage flexComponent={flexComponent} context={context} />
                  <UitkScrim type={viewProps.cardScrimType}>
                    <h3 className={viewProps.cardClassName}>{viewProps.callToActionLabel}</h3>
                    <div className="uitk-type-left uitk-type-subheading">{viewProps.callToActionSubLabel}</div>
                  </UitkScrim>
                </UitkFigure>
                {viewProps.showEditorialContent && (
                  <UitkCardAlohaContentSection>
                    <UitkText size={200}>
                      <div
                        className="uitk-type-left"
                        dangerouslySetInnerHTML={{ __html: flexComponent.model.callToAction.text }}
                      />
                    </UitkText>
                  </UitkCardAlohaContentSection>
                )}
              </>
            )}
            {!flexComponent.model.nextScene && (
              <UitkCardAlohaLink>
                <a data-testid="call-to-action-link" href={viewProps.callToActionLink}>
                  {viewProps.callToActionTitle}
                </a>
              </UitkCardAlohaLink>
            )}
          </UitkCardAloha>
        </div>
      );
    })
  )
);

export default CallToAction;
